import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  CircularProgress,
  TextField,
} from '@mui/material';
import api from '../../config/api';
import { useTranslation } from 'react-i18next';
import useStore from '../../config/store';

/**
 * Confirmación para eliminar una vacante. En el backend es un borrado lógico:
 * la vacante se oculta en ATS y en Gestión de Cargos y deja de recibir
 * postulaciones; los candidatos y su historial se conservan (se recupera con
 * scripts/restorePosition.js del backend). Solo se monta para quien puede
 * editar la vacante. Lo usan el ATS y Gestión de Cargos.
 *
 * Para evitar borrar con un clic, "Eliminar" se habilita recién al escribir la
 * palabra de confirmación (ELIMINAR / DELETE) y se muestra cuántos candidatos
 * tiene la vacante.
 *
 * Props:
 * - open, onClose
 * - positionId: vacante a eliminar
 * - positionName: nombre que se muestra en la confirmación
 * - candidatesCount: candidatos de la vacante (null = no se muestra)
 * - onDeleted: (positionId) => void  (sacarla de la tabla o volver a la lista)
 */
const AtsDeletePositionDialog = ({ open, onClose, positionId, positionName, candidatesCount = null, onDeleted }) => {
  const { t } = useTranslation();
  const { authToken, showAlert } = useStore.getState();
  const [deleting, setDeleting] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const confirmWord = t('ats.positions.deleteDialog.confirmWord');
  const confirmed = confirmText.trim().toUpperCase() === confirmWord.toUpperCase();

  // Cada apertura parte con el campo vacío
  useEffect(() => {
    if (open) setConfirmText('');
  }, [open]);

  const handleConfirm = async () => {
    if (!confirmed) return;
    try {
      setDeleting(true);
      const response = await api.delete(`/positions/${positionId}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (response.data.response?.status_response === 'OK') {
        showAlert(t('ats.positions.deleteDialog.success'), 'success');
        onClose();
        if (onDeleted) onDeleted(positionId);
      } else {
        showAlert(t('ats.positions.deleteDialog.error'), 'error');
      }
    } catch (err) {
      console.error('Error eliminando vacante:', err);
      showAlert(t('ats.positions.deleteDialog.error'), 'error');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open={open} onClose={deleting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('ats.positions.deleteDialog.title')}</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ color: 'text.primary' }}>
          {t('ats.positions.deleteDialog.confirm', { name: positionName || '' })}
        </DialogContentText>
        {candidatesCount !== null && candidatesCount !== undefined && (
          <DialogContentText sx={{ mt: 1, fontWeight: candidatesCount > 0 ? 600 : 400, color: candidatesCount > 0 ? '#B45309' : 'text.secondary' }}>
            {candidatesCount > 0
              ? t('ats.positions.deleteDialog.candidates', { count: candidatesCount })
              : t('ats.positions.deleteDialog.noCandidates')}
          </DialogContentText>
        )}
        <DialogContentText sx={{ mt: 1 }}>
          {t('ats.positions.deleteDialog.warning')}
        </DialogContentText>
        <TextField
          autoFocus
          fullWidth
          size="small"
          sx={{ mt: 2 }}
          label={t('ats.positions.deleteDialog.typeToConfirm', { word: confirmWord })}
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && confirmed && !deleting) handleConfirm(); }}
          disabled={deleting}
          inputProps={{ autoComplete: 'off' }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit" disabled={deleting}>
          {t('cancel')}
        </Button>
        <Button
          onClick={handleConfirm}
          color="error"
          variant="contained"
          disabled={deleting || !confirmed}
          startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {t('delete')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AtsDeletePositionDialog;
